import { useState, useCallback, useEffect, useRef } from "react";
import { useRouter } from "nextjs-toploader/app";
import { useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  markAllAsRead,
  markAsRead,
} from "@/services/notification.service";
import {
  GetNotificationsResponse,
  NotificationItem,
} from "@/interface/notification.types";
import { showDynamicToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import useHeader from "../useHeader";

interface UseNotificationListReturn {
  notifications: NotificationItem[];
  loading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  fetchMoreNotifications: () => void;
  refreshNotifications: () => void;
  totalCount: number;
  handleMarkAllAsRead: () => Promise<void>;
  handleMarkAsRead: (notificationId: string) => Promise<void>;
  handleNotificationClick: (notification: NotificationItem, onNavigate?: () => void) => void;
  expandedIds: string[];
  toggleExpand: (id: string) => void;
}

interface UseNotificationListProps {
  userType?: "Contractor" | "Homeowner";
  isOpen?: boolean;
}

export const useNotificationList = (props?: UseNotificationListProps): UseNotificationListReturn => {
  const userType = props?.userType || "Contractor";
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isLoggedIn } = useHeader();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const loadingRef = useRef(false);
  const fetchingPageRef = useRef<number | null>(null);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const fetchNotifications = useCallback(
    async (pageNum: number = 1, isRefresh: boolean = false) => {
      if (!isLoggedIn) return;
      if (
        loadingRef.current ||
        (fetchingPageRef.current === pageNum && !isRefresh)
      )
        return;

      fetchingPageRef.current = pageNum;
      loadingRef.current = true;
      if (pageNum === 1 && !isRefresh) {
        setLoading(true);
      } else if (pageNum > 1) {
        setIsLoadingMore(true);
      }
      setError(null);

      try {
        const response = await getNotifications({
          pageNumber: pageNum,
          pageSize: 10,
          query: "",
          filters: "",
          sorts: "",
        });

        if (isRefresh || pageNum === 1) {
          setNotifications(response.data.data);
        } else {
          setNotifications((prev) => {
            const newNotifications = response.data.data.filter(
              (newItem) => !prev.some((prevItem) => prevItem.id === newItem.id)
            );
            return [...prev, ...newNotifications];
          });
        }

        setTotalCount(response.data.count);

        const totalPages = response.meta.pagination.totalPages;
        setHasMore(pageNum < totalPages);

        if (!isRefresh) {
          setPage(pageNum);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch notifications"
        );
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
        loadingRef.current = false;
        if (isRefresh) {
          fetchingPageRef.current = null;
        }
      }
    },
    [isLoggedIn]
  );

  const fetchMoreNotifications = useCallback(() => {
    if (hasMore && !loading && !isLoadingMore) {
      fetchNotifications(page + 1);
    }
  }, [hasMore, loading, isLoadingMore, page, fetchNotifications]);

  const refreshNotifications = useCallback(() => {
    setPage(1);
    fetchNotifications(1, true);
  }, [fetchNotifications]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      // Optimistically clear all unread counts
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setTotalCount(0);
      
      // Optimistically update the badge count in React Query cache
      queryClient.setQueryData(["unreadNotificationCount"], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: {
            ...oldData.data,
            count: 0,
          },
        };
      });

      await markAllAsRead();
      
      // Invalidate notification count query to update badges in the UI from server
      await queryClient.invalidateQueries({ queryKey: ["unreadNotificationCount"] });
      // Refresh the local list to get final state
      refreshNotifications();
      showDynamicToast(TOAST_TYPES.success, "Success", "All notifications have been marked as read");
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      showDynamicToast(TOAST_TYPES.error, "Error", "Failed to mark all notifications as read");
      refreshNotifications();
    }
  }, [refreshNotifications, queryClient]);

  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    try {
      // Find the notification to check if it was unread
      const notification = notifications.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.isRead;

      // Optimistically update the notification as read in the local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, isRead: true }
            : n
        )
      );
      
      if (wasUnread) {
        // Optimistically update local count
        setTotalCount(prev => Math.max(0, prev - 1));
        
        // Optimistically update the badge count in React Query cache
        queryClient.setQueryData(["unreadNotificationCount"], (oldData: any) => {
          if (!oldData || !oldData.data) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              count: Math.max(0, (oldData.data.count || 0) - 1),
            },
          };
        });
      }
      
      await markAsRead(notificationId);
      
      // Invalidate notification count query to update badges in the UI from server
      await queryClient.invalidateQueries({ queryKey: ["unreadNotificationCount"] });
      
      // Refresh the list to get the updated state from the server
      refreshNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      // Revert optimistic update on error by refreshing the list
      refreshNotifications();
      queryClient.invalidateQueries({ queryKey: ["unreadNotificationCount"] });
    }
  }, [notifications, refreshNotifications, queryClient]);

  const handleNotificationClick = useCallback(async (notification: NotificationItem, onNavigate?: () => void) => {
    // Trigger logo animation
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("nextjs-toploader:start"));
    }

    // Mark notification as read if it's unread
    if (!notification.isRead) {
      await handleMarkAsRead(notification.id);
    }

    if (notification?.data?.ProjectId) {
      if (onNavigate) {
        onNavigate();
      }
      
      if (userType === "Homeowner") {
        // For homeowners, navigate to proposal page if ProposalId exists
        if (notification.data.ProposalId) {
          router.push(`/homeowner/projects/${notification.data.ProjectId}/proposals/${notification.data.ProposalId}`);
        } else {
          router.push(`/homeowner/projects/${notification.data.ProjectId}`);
        }
      } else {
        // For contractors, check notification type
        if (notification.notificationType === "ReviewReceived") {
          router.push(`/contractor/reviews?projectId=${notification.data.ProjectId}`);
        } else {
          router.push(`/contractor/projects/${notification.data.ProjectId}`);
        }
      }
    }
  }, [router, userType, handleMarkAsRead]);

  // Initial fetch
  useEffect(() => {
    if (!isLoggedIn) return;
    fetchNotifications(1);
  }, [isLoggedIn, fetchNotifications]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (props?.isOpen) {
      refreshNotifications();
    }
  }, [props?.isOpen, refreshNotifications]);

  return {
    notifications,
    loading,
    isLoadingMore,
    error,
    hasMore,
    fetchMoreNotifications,
    refreshNotifications,
    totalCount,
    handleMarkAllAsRead,
    handleMarkAsRead,
    handleNotificationClick,
    expandedIds,
    toggleExpand,
  };
};
